/**
 * Iter 122 · Tienda online pública de pegatinas
 *
 * Ruta pública `/tienda/pegatinas` — sin auth. Cliente final:
 *  1. Elige material
 *  2. Añade diseños (medidas + cantidad)
 *  3. Ve precio con desglose (subtotal, envío, IVA, total)
 *  4. Completa datos de envío
 *  5. Paga con Redsys (formulario POST auto-submit)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Sticker, Plus, Trash2, ShoppingCart, CreditCard, ShieldCheck, Truck, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cliente axios sin credenciales para endpoints públicos (evita CORS con cookies)
const publicApi = axios.create({ baseURL: API, withCredentials: false });

const DISENO_VACIO = { nombre: "Diseño 1", ancho_cm: 8, alto_cm: 3, cantidad: 200, corte_unitario: false };

export default function TiendaPegatinas() {
  const [config, setConfig] = useState(null);
  const [materiales, setMateriales] = useState([]);
  const [step, setStep] = useState(1);           // 1 = producto · 2 = envío/pago
  const [form, setForm] = useState({
    material_id: "",
    disenos: [{ ...DISENO_VACIO }],
  });
  const [cot, setCot] = useState(null);
  const [cotLoading, setCotLoading] = useState(false);
  const [envio, setEnvio] = useState({
    nombre: "", email: "", telefono: "",
    direccion: "", poblacion: "", provincia: "", cp: "", pais: "España",
    nif: "", notas: "", acepta_privacidad: false,
  });
  const [payFields, setPayFields] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const payFormRef = useRef(null);

  // Cargar config
  useEffect(() => {
    publicApi.get("/public/tienda/config").then((r) => {
      setConfig(r.data);
      setMateriales(r.data.materiales || []);
      if ((r.data.materiales || []).length && !form.material_id) {
        setForm((f) => ({ ...f, material_id: r.data.materiales[0].id }));
      }
    }).catch(() => {
      toast.error("Tienda no disponible temporalmente");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cotización con debounce
  useEffect(() => {
    if (!form.material_id) return;
    const valido = form.disenos.every(
      (d) => Number(d.ancho_cm) > 0 && Number(d.alto_cm) > 0 && Number(d.cantidad) > 0
    );
    if (!valido) { setCot(null); return; }
    const t = setTimeout(async () => {
      setCotLoading(true);
      try {
        const payload = {
          material_id: form.material_id,
          disenos: form.disenos.map((d) => ({
            nombre: d.nombre || "Diseño",
            ancho_cm: Number(d.ancho_cm),
            alto_cm: Number(d.alto_cm),
            cantidad: Number(d.cantidad),
            corte_unitario: !!d.corte_unitario,
          })),
        };
        const r = await publicApi.post("/public/tienda/cotizar", payload);
        setCot(r.data);
      } catch {
        setCot(null);
      }
      setCotLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [form]);

  // Auto-submit del formulario Redsys
  useEffect(() => {
    if (payFields && payFormRef.current) {
      payFormRef.current.submit();
    }
  }, [payFields]);

  const updateDiseno = (idx, patch) => {
    setForm((f) => ({ ...f, disenos: f.disenos.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));
  };
  const addDiseno = () => setForm((f) => ({
    ...f,
    disenos: [...f.disenos, { ...DISENO_VACIO, nombre: `Diseño ${f.disenos.length + 1}` }],
  }));
  const removeDiseno = (idx) => setForm((f) => ({ ...f, disenos: f.disenos.filter((_, i) => i !== idx) }));

  const minPedido = config?.min_pedido_eur || 15;
  const puedeContinuar = cot && cot.subtotal_con_descuento >= minPedido;

  const irACheckout = () => {
    if (!puedeContinuar) return toast.error(`Pedido mínimo ${minPedido.toFixed(2)}€ (sin IVA)`);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const iniciarPago = async () => {
    // Validaciones básicas
    const req = ["nombre", "email", "telefono", "direccion", "poblacion", "provincia", "cp"];
    for (const k of req) {
      if (!envio[k] || !String(envio[k]).trim()) {
        return toast.error(`Falta el campo: ${k}`);
      }
    }
    if (!envio.acepta_privacidad) return toast.error("Debes aceptar la política de privacidad");
    if (!/^\S+@\S+\.\S+$/.test(envio.email)) return toast.error("Email no válido");

    setCreatingOrder(true);
    try {
      const payload = {
        material_id: form.material_id,
        disenos: form.disenos.map((d) => ({
          nombre: d.nombre || "Diseño",
          ancho_cm: Number(d.ancho_cm),
          alto_cm: Number(d.alto_cm),
          cantidad: Number(d.cantidad),
          corte_unitario: !!d.corte_unitario,
        })),
        direccion: envio,
      };
      const r = await publicApi.post("/public/tienda/pedidos", payload);
      // Guardar order en localStorage para la página de resultado
      try { localStorage.setItem("dnf_last_tienda_order", r.data.order); } catch { /* noop */ }
      setPayFields({ action: r.data.action, fields: r.data.fields });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar el pago");
      setCreatingOrder(false);
    }
  };

  return (
    <div className="tienda-scope min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-bold text-lg" data-testid="tienda-logo">
            <Sticker className="w-6 h-6 text-fuchsia-600" />
            <span>RotulaciónDNF · Pegatinas</span>
          </a>
          <div className="hidden md:flex items-center gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Pago seguro Redsys</span>
            <span className="flex items-center gap-1"><Truck className="w-4 h-4 text-blue-600" /> Envío 3-5 días</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      {step === 1 && (
        <section className="max-w-6xl mx-auto px-4 py-8 md:py-12 text-center">
          <div className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 text-fuchsia-800 px-3 py-1 text-xs font-medium mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Presupuesto instantáneo
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="tienda-titulo">
            {config?.titulo || "Pegatinas personalizadas al instante"}
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto text-base">
            {config?.subtitulo || "Calcula, paga y recibe."}
          </p>
        </section>
      )}

      <main className="max-w-6xl mx-auto px-4 pb-20">
        {step === 1 ? (
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
            {/* Configurador */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5 space-y-5">
                <h2 className="text-lg font-semibold">1. Elige tu vinilo</h2>
                {materiales.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No hay materiales disponibles ahora mismo.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tienda-materiales-grid">
                    {materiales.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setForm({ ...form, material_id: m.id })}
                        data-testid={`tienda-material-${m.id}`}
                        className={`text-left p-3 rounded-lg border-2 transition ${
                          form.material_id === m.id
                            ? "border-fuchsia-600 bg-fuchsia-50 ring-2 ring-fuchsia-200"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                      >
                        <div className="font-medium text-sm">{m.nombre}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Desde {m.pvp_m2_aprox?.toFixed(2)}€/m² · sin IVA
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold">2. Diseños y cantidades</h2>
                    <Button size="sm" variant="outline" onClick={addDiseno} data-testid="tienda-add-diseno">
                      <Plus className="w-4 h-4 mr-1" /> Añadir diseño
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {form.disenos.map((d, idx) => (
                      <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-slate-50/60"
                        data-testid={`tienda-diseno-${idx}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Input value={d.nombre}
                            onChange={(e) => updateDiseno(idx, { nombre: e.target.value })}
                            className="bg-white h-8 text-sm" placeholder="Nombre del diseño" />
                          {form.disenos.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => removeDiseno(idx)}
                              className="text-red-500 h-8 w-8 p-0" data-testid={`tienda-remove-diseno-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[11px] text-slate-500">Ancho (cm)</Label>
                            <Input type="number" step="0.1" min="0.5" value={d.ancho_cm}
                              onChange={(e) => updateDiseno(idx, { ancho_cm: e.target.value })}
                              data-testid={`tienda-diseno-ancho-${idx}`} className="bg-white h-9" />
                          </div>
                          <div>
                            <Label className="text-[11px] text-slate-500">Alto (cm)</Label>
                            <Input type="number" step="0.1" min="0.5" value={d.alto_cm}
                              onChange={(e) => updateDiseno(idx, { alto_cm: e.target.value })}
                              data-testid={`tienda-diseno-alto-${idx}`} className="bg-white h-9" />
                          </div>
                          <div>
                            <Label className="text-[11px] text-slate-500">Cantidad</Label>
                            <Input type="number" step="1" min="1" value={d.cantidad}
                              onChange={(e) => updateDiseno(idx, { cantidad: e.target.value })}
                              data-testid={`tienda-diseno-cantidad-${idx}`} className="bg-white h-9" />
                          </div>
                        </div>
                        <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={!!d.corte_unitario}
                            onChange={(e) => updateDiseno(idx, { corte_unitario: e.target.checked })}
                            data-testid={`tienda-diseno-corte-${idx}`} />
                          Cortadas de 1 en 1 (+coste). Por defecto: en tira/bobina.
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 leading-relaxed">
                  {config?.aviso_legal}
                </p>
              </CardContent>
            </Card>

            {/* Resumen */}
            <div className="lg:sticky lg:top-24 self-start">
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingCart className="w-5 h-5 text-fuchsia-600" />
                    <h2 className="text-lg font-semibold">Tu pedido</h2>
                  </div>
                  {cotLoading && <div className="text-xs text-slate-500">Calculando…</div>}
                  {!cot && !cotLoading && (
                    <div className="text-sm text-slate-500">Completa los datos para ver el precio.</div>
                  )}
                  {cot && (
                    <div data-testid="tienda-resumen">
                      <div className="text-xs text-slate-500 mb-2">
                        {cot.material_nombre} · {cot.cantidad_total} uds · {cot.m2_total} m²
                      </div>
                      <MaquetaBobina cot={cot} />
                      <div className="border-t border-slate-200 mt-3 pt-3 space-y-1.5 text-sm">
                        <Row k="Subtotal" v={fmt(cot.subtotal_sin_iva)} />
                        {cot.descuento_pct > 0 && (
                          <Row k={`Descuento (-${cot.descuento_pct}%)`} v={`-${fmt(cot.subtotal_sin_iva - cot.subtotal_con_descuento)}`} accent="text-emerald-600" />
                        )}
                        <Row k="Envío" v={cot.gastos_envio === 0 ? "Gratis" : fmt(cot.gastos_envio)} accent={cot.gastos_envio === 0 ? "text-emerald-600 font-semibold" : ""} />
                        <Row k={`IVA (${cot.iva_pct}%)`} v={fmt(cot.iva)} />
                        <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between text-lg font-bold" data-testid="tienda-total">
                          <span>Total</span>
                          <span>{fmt(cot.total)}</span>
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Precio unitario: {fmt(cot.precio_unitario)} · sin IVA
                        </div>
                      </div>
                      {!puedeContinuar && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
                          Pedido mínimo {minPedido.toFixed(2)}€ sin IVA.
                        </p>
                      )}
                      <Button
                        onClick={irACheckout}
                        disabled={!puedeContinuar}
                        className="w-full mt-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-11"
                        data-testid="tienda-continuar-btn">
                        Continuar al pago →
                      </Button>
                      <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Pago seguro</span>
                        <span>·</span>
                        <span>Envío en 3-5 días</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          // Step 2: Envío + pago
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Datos de envío</h2>
                  <Button size="sm" variant="ghost" onClick={() => setStep(1)} data-testid="tienda-back-btn">
                    ← Volver
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field k="Nombre completo *" testId="envio-nombre">
                    <Input value={envio.nombre} onChange={(e) => setEnvio({ ...envio, nombre: e.target.value })}
                      data-testid="envio-nombre" />
                  </Field>
                  <Field k="Email *" testId="envio-email">
                    <Input type="email" value={envio.email} onChange={(e) => setEnvio({ ...envio, email: e.target.value })}
                      data-testid="envio-email" />
                  </Field>
                  <Field k="Teléfono *">
                    <Input value={envio.telefono} onChange={(e) => setEnvio({ ...envio, telefono: e.target.value })}
                      data-testid="envio-telefono" />
                  </Field>
                  <Field k="NIF/CIF (opcional)">
                    <Input value={envio.nif} onChange={(e) => setEnvio({ ...envio, nif: e.target.value })}
                      data-testid="envio-nif" />
                  </Field>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Dirección *</Label>
                    <Input value={envio.direccion} onChange={(e) => setEnvio({ ...envio, direccion: e.target.value })}
                      data-testid="envio-direccion" />
                  </div>
                  <Field k="Población *">
                    <Input value={envio.poblacion} onChange={(e) => setEnvio({ ...envio, poblacion: e.target.value })}
                      data-testid="envio-poblacion" />
                  </Field>
                  <Field k="Provincia *">
                    <Input value={envio.provincia} onChange={(e) => setEnvio({ ...envio, provincia: e.target.value })}
                      data-testid="envio-provincia" />
                  </Field>
                  <Field k="Código postal *">
                    <Input value={envio.cp} onChange={(e) => setEnvio({ ...envio, cp: e.target.value })}
                      data-testid="envio-cp" />
                  </Field>
                  <Field k="País">
                    <Input value={envio.pais} onChange={(e) => setEnvio({ ...envio, pais: e.target.value })} />
                  </Field>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Comentarios (opcional)</Label>
                    <Textarea value={envio.notas} onChange={(e) => setEnvio({ ...envio, notas: e.target.value })}
                      rows={2} data-testid="envio-notas" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={envio.acepta_privacidad}
                    onChange={(e) => setEnvio({ ...envio, acepta_privacidad: e.target.checked })}
                    data-testid="envio-privacidad" />
                  <span>
                    He leído y acepto la <a href="/privacidad" target="_blank" rel="noreferrer" className="text-fuchsia-600 underline">política de privacidad</a>.
                    Autorizo el tratamiento de mis datos para procesar el pedido.
                  </span>
                </label>
              </CardContent>
            </Card>

            <div className="lg:sticky lg:top-24 self-start">
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-fuchsia-600" /> Resumen y pago
                  </h2>
                  {cot && (
                    <div className="space-y-1.5 text-sm">
                      <div className="text-xs text-slate-500 mb-2">
                        {cot.material_nombre} · {cot.cantidad_total} uds
                      </div>
                      <Row k="Subtotal" v={fmt(cot.subtotal_con_descuento)} />
                      <Row k="Envío" v={cot.gastos_envio === 0 ? "Gratis" : fmt(cot.gastos_envio)} />
                      <Row k={`IVA (${cot.iva_pct}%)`} v={fmt(cot.iva)} />
                      <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span>{fmt(cot.total)}</span>
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={iniciarPago}
                    disabled={creatingOrder}
                    className="w-full mt-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-11"
                    data-testid="tienda-pagar-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    {creatingOrder ? "Redirigiendo…" : `Pagar ${cot ? fmt(cot.total) : ""}`}
                  </Button>
                  <div className="text-[11px] text-slate-500 text-center mt-3">
                    Serás redirigido a la pasarela segura de Redsys (LaCaixa).
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Redsys hidden auto-submit form */}
      {payFields && (
        <form ref={payFormRef} method="POST" action={payFields.action} style={{ display: "none" }}>
          {Object.entries(payFields.fields).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v} />
          ))}
        </form>
      )}

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} RotulaciónDNF · <a href="/privacidad" className="underline">Privacidad</a>
      </footer>
    </div>
  );
}

const fmt = (n) => `${Number(n || 0).toFixed(2)}€`;

const Row = ({ k, v, accent = "" }) => (
  <div className="flex justify-between">
    <span className="text-slate-600">{k}</span>
    <span className={accent || "text-slate-900"}>{v}</span>
  </div>
);

const Field = ({ k, children }) => (
  <div>
    <Label className="text-xs text-slate-600">{k}</Label>
    {children}
  </div>
);

/**
 * Maqueta visual · rectángulo con las dimensiones del cliente sobre un rollo con proporciones reales.
 * Muestra el aprovechamiento de bobina: cuántas piezas caben por fila y cuántas filas se imprimen.
 */
function MaquetaBobina({ cot }) {
  const d = cot?.disenos?.[0];
  const [maxW, maxH] = [280, 240];   // tamaño máx del svg
  const svg = useMemo(() => {
    if (!d || !d.piezas_por_fila || !d.filas) return null;
    const anchoBobina = cot.ancho_bobina_cm || 137;
    const largoTotal = d.largo_con_merma_cm || 100;
    // Escala uniforme para que quepa dentro de maxW × maxH manteniendo proporciones reales
    const escala = Math.min(maxW / anchoBobina, maxH / Math.max(largoTotal, anchoBobina * 0.4));
    const svgW = anchoBobina * escala;
    const svgH = Math.max(120, largoTotal * escala);
    // Tamaño real de cada pegatina en el svg
    const pieceW = d.ancho_cm * escala;
    const pieceH = d.alto_cm * escala;
    const gutter = 0.2 * escala;   // gutter 2mm
    const pieces = [];
    // Distribuimos las piezas centradas y sin cortarlas si sobran (visualmente basta con d.filas × d.piezas_por_fila reales)
    let count = 0;
    for (let f = 0; f < d.filas; f += 1) {
      for (let c = 0; c < d.piezas_por_fila; c += 1) {
        if (count >= d.cantidad) break;
        const x = c * (pieceW + gutter) + gutter;
        const y = f * (pieceH + gutter) + gutter;
        pieces.push(
          <rect key={`${f}-${c}`} x={x} y={y} width={pieceW} height={pieceH}
            rx={Math.min(2, pieceW * 0.08)} ry={Math.min(2, pieceH * 0.08)}
            fill="url(#stickerGrad)" stroke="#c026d3" strokeWidth="0.6" />
        );
        count += 1;
      }
    }
    return { svgW, svgH, pieces };
  }, [cot, d]);

  if (!svg) return null;
  return (
    <div className="bg-gradient-to-b from-slate-100 to-white border border-slate-200 rounded-lg p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] font-medium text-slate-600">Maqueta · a escala real</div>
        <div className="text-[10px] text-slate-500">
          {d.piezas_por_fila}/fila × {d.filas} filas
        </div>
      </div>
      <div className="flex justify-center bg-white rounded border border-slate-200 py-2 relative">
        <svg width={svg.svgW} height={svg.svgH} viewBox={`0 0 ${svg.svgW} ${svg.svgH}`}
          data-testid="tienda-maqueta-svg" style={{ maxWidth: "100%" }}>
          <defs>
            <linearGradient id="stickerGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f5d0fe" />
              <stop offset="100%" stopColor="#e879f9" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="#fafafa" />
          {svg.pieces}
        </svg>
        {/* Reglas laterales */}
        <div className="absolute -left-1 top-2 bottom-2 flex flex-col justify-between text-[9px] text-slate-400 pointer-events-none">
          <span>0</span>
          <span>{Math.round(d.largo_con_merma_cm)}cm</span>
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mt-1.5 text-center">
        Cada pegatina mide <b className="text-slate-700">{d.ancho_cm}×{d.alto_cm} cm</b> · bobina {cot.ancho_bobina_cm}cm
      </div>
    </div>
  );
}

// Silence unused import
void useMemo;
